import { useCallback, useMemo } from "react";
import { t } from "ttag";

import {
  executeRowAction,
  reloadDashboardCards,
} from "metabase/dashboard/actions";
import {
  getEditingDashcardId,
  getParameterValues,
} from "metabase/dashboard/selectors";
import { getActionIsEnabledInDatabase } from "metabase/dashboard/utils";
import { connect } from "metabase/redux";
import type { Dispatch, State } from "metabase/redux/store";
import { Tooltip } from "metabase/ui";
import type { VisualizationProps } from "metabase/visualizations/types";
import type {
  ActionDashboardCard,
  Dashboard,
  ParameterValuesMap,
  ParametersForActionExecution,
  WritebackAction,
} from "metabase-types/api";

import { FullContainer } from "./ActionButton.styled";
import ActionButtonView from "./ActionButtonView";
import ActionVizForm from "./ActionVizForm";
import {
  getDashcardParamValues,
  getMappedActionParameters,
  getNotProvidedActionParameters,
  shouldShowConfirmation,
} from "./utils";

interface OwnProps {
  dashcard: ActionDashboardCard;
  dashboard: Dashboard;
  parameterValues: ParameterValuesMap;
}

interface StateProps {
  isEditingDashcard: boolean;

  dispatch: Dispatch;
}

export type ActionProps = Pick<VisualizationProps, "settings" | "isSettings"> &
  OwnProps &
  StateProps;

const ActionComponent = ({
  dashcard,
  dashboard,
  dispatch,
  isSettings,
  settings,
  parameterValues,
  isEditingDashcard,
}: ActionProps) => {
  const actionSettings = dashcard.action?.visualization_settings;
  const actionDisplayType =
    settings?.actionDisplayType ?? actionSettings?.type ?? "button";

  const dashcardParamValues = useMemo(
    () => getDashcardParamValues(dashcard, parameterValues),
    [dashcard, parameterValues],
  );

  const missingParameters = useMemo(() => {
    if (!dashcard.action) {
      return [];
    }
    return getNotProvidedActionParameters(
      dashcard.action,
      dashcardParamValues ?? [],
    );
  }, [dashcard, dashcardParamValues]);

  const mappedParameters = useMemo(() => {
    if (!dashcard.action) {
      return [];
    }
    return getMappedActionParameters(
      dashcard.action,
      dashcardParamValues ?? [],
    );
  }, [dashcard, dashcardParamValues]);

  const shouldConfirm = shouldShowConfirmation(dashcard?.action);

  const shouldDisplayButton = !!(
    actionDisplayType !== "form" ||
    !missingParameters.length ||
    shouldConfirm
  );

  const onSubmit = useCallback(
    async (parameters: ParametersForActionExecution) => {
      const result = await executeRowAction({
        dashboard,
        dashcard,
        parameters,
        dispatch,
        shouldToast: shouldDisplayButton,
      });

      if (result.success) {
        dispatch(reloadDashboardCards());
      }

      return result;
    },
    [dashboard, dashcard, dispatch, shouldDisplayButton],
  );

  return (
    <ActionVizForm
      // Unjustified type cast. FIXME
      action={dashcard.action as WritebackAction}
      dashboard={dashboard}
      dashcard={dashcard}
      missingParameters={missingParameters}
      mappedParameters={mappedParameters}
      dashcardParamValues={dashcardParamValues}
      settings={settings}
      isSettings={isSettings}
      shouldDisplayButton={shouldDisplayButton}
      isEditingDashcard={isEditingDashcard}
      canEditAction={dashcard.action?.can_write === true}
      onSubmit={onSubmit}
    />
  );
};

const ConnectedActionComponent = connect()(ActionComponent);

function mapStateToProps(state: State, props: ActionProps) {
  return {
    parameterValues: getParameterValues(state),
    isEditingDashcard: getEditingDashcardId(state) === props.dashcard.id,
  };
}

function ActionFn(props: ActionProps) {
  const { dashcard } = props;
  const { action } = dashcard;

  const hasActionsEnabled = getActionIsEnabledInDatabase(dashcard);
  const isArchived = action?.archived === true;

  if (!action || !hasActionsEnabled || isArchived) {
    const tooltip = getErrorTooltip({
      hasActionAssigned: !!action,
      hasActionsEnabled,
      isArchived,
    });

    return (
      <Tooltip label={tooltip}>
        <FullContainer data-testid="action-button-full-container">
          <ActionButtonView
            disabled
            icon="bolt"
            tooltip={tooltip}
            settings={props.settings}
            focus={props.isEditingDashcard}
          />
        </FullContainer>
      </Tooltip>
    );
  }

  return <ConnectedActionComponent {...props} />;
}

function getErrorTooltip({
  hasActionAssigned,
  hasActionsEnabled,
  isArchived,
}: {
  hasActionAssigned: boolean;
  hasActionsEnabled: boolean;
  isArchived: boolean;
}) {
  if (!hasActionAssigned) {
    return t`No action assigned`;
  }

  if (isArchived) {
    return t`This action is archived`;
  }

  if (!hasActionsEnabled) {
    return t`Actions are not enabled for this database`;
  }

  return t`Something’s gone wrong`;
}

export const Action = connect(mapStateToProps)(ActionFn);
